import snakecaseKeys from 'snakecase-keys';

import api from './api';
import { stripEmptyProps, stripUndefinedProps } from '/lib/utils/strippers';
import RichTextType from '/front/types/frontRichTextType';

const offerApi = {
    create: (data: any) => api.post('offer', snakecaseKeys(data || {})),

    createFromScratch: (payload: {
        customer_uuid: string;
        caseworker_uuid?: string;
        company_id?: number;
        post_code?: string;
        address?: string;
    }) => api.post('offer/scratch', stripUndefinedProps(payload)),

    getByRef: (ref: string, token: string) =>
        api.getSecure('offer/' + ref, token),

    getAll: ({ token }: { token: string }) => api.getSecure(`offer`, token),

    getReservation: (reservationUuid: string) =>
        api.get(`schedule/reservation/${reservationUuid}`),

    getAllByTab: ({
        tab,
        token,
        page,
        caseworkerUuid,
        electricianOffers,
        query,
    }: {
        tab: string;
        token: string;
        page: number;
        caseworkerUuid?: string;
        electricianOffers?: boolean;
        query?: string;
    }) =>
        api.getSecure(
            `offer/tab/${tab}?page=${page}&per_page=15${
                caseworkerUuid ? '&caseworker_uuid=' + caseworkerUuid : ''
            }${electricianOffers ? '&electrician_offers=true' : ''}${
                query ? '&query=' + query : ''
            }`,
            token
        ),

    search: (query: string) => api.getSecure(`offer/search?query=${query}`),

    send: (offerId: number) => api.post(`offer/${offerId}/send-offer`),

    decline: (offerId: number, reason: any) =>
        api.post(`offer/${offerId}/decline`, { reason }),

    delete: (offerId: number) => api.post(`offer/${offerId}/delete`),

    requestRenewal: (offerId: number) =>
        api.post(`offer/${offerId}/request-renewal`),

    accept: (offerId: number, reservationUuid: string) =>
        api.post(`offer/${offerId}/accept`, {
            reservation_uuid: reservationUuid,
        }),

    clearNoTimeslotsConcern: (offerId: number) =>
        api.post(`offer/${offerId}/clear-no-time-slots-concern`),

    disconnectElectrician: (offerId: number) =>
        api.post(`offer/${offerId}/remove-electrician`),

    getTimeSlots: ({
        offerId,
        cartUuid,
        page,
        postCode,
    }: {
        offerId: number;
        cartUuid: string;
        page: number;
        postCode: string;
    }) =>
        api.post(`offer/${offerId}/availability`, {
            page,
            cart_uuid: cartUuid,
            post_code: postCode,
        }),

    update: (offerId: number, offer: any) =>
        api.patch(`offer/${offerId}`, offer),

    changeExpirationTime: (offerId: number, date: string) =>
        api.post(`offer/${offerId}/extend`, { date }),

    addComment: (offerId: number, message: string) =>
        api.post(`offer/${offerId}/comment`, { message }),

    createLineItem: (offerId: number, lineItem: any) =>
        api.post(`offer/${offerId}/line-item`, lineItem),

    draft: {
        create: (ref: string) => api.post(`offer/${ref}/draft`),

        get: (ref: string) => api.getSecure(`offer/${ref}/draft`),

        delete: (offerId: number) => api.delete(`offer/${offerId}/draft`),

        createLineItem: (offerId: number, lineItem: any) =>
            api.post(`offer/${offerId}/draft/line-item`, lineItem),

        updateLineItem: (offerId: number, lineItemId: number, lineItem: any) =>
            api.patch(
                `offer/${offerId}/draft/line-item/${lineItemId}`,
                lineItem
            ),

        updateLineItemName: (
            offerId: number,
            lineItemId: number,
            name: string
        ) =>
            api.patch(`offer/${offerId}/draft/line-item/${lineItemId}/name`, {
                name,
            }),

        deleteLineItem: (offerId: number, lineItemId: number) =>
            api.delete(`offer/${offerId}/draft/line-item/${lineItemId}`),

        convertToCustom: (projectId: number, lineItemId: number) =>
            api.post(
                `offer/${projectId}/draft/line-item/${lineItemId}/convert`
            ),

        cancelJob: (offerId: number, jobId: number, reason: any) =>
            api.post(`offer/${offerId}/draft/line-item/${jobId}/cancel`, {
                reason,
            }),

        restoreCancelledJob: (offerId: number, jobId: number) =>
            api.post(`offer/${offerId}/draft/line-item/${jobId}/re-enable`, {}),

        update: (offerId: number, introContent: RichTextType | null) =>
            api.patch(
                `offer/${offerId}/draft`,
                stripEmptyProps({ intro_content: introContent })
            ),

        mergeAndSendForApproval: (offerId: number) =>
            api.post(`offer/${offerId}/draft/merge `),

        getDrafts: (offerId: number) =>
            api.getSecure(`offer/${offerId}/draft/list`),

        getDraftById: (offerId: number, draftId: number) =>
            api.getSecure(`offer/${offerId}/draft/${draftId}`),

        updateLineItemOrder: (
            offerId: number,
            idOrderMapping: { [id: string]: number }
        ) => api.post(`offer/${offerId}/draft/line-item/order`, idOrderMapping), // { id: order }
    },
};

export default offerApi;
